import { action, makeObservable, observable } from "mobx";

/**
 * QueueState is a simple queue implementation that can be used to store
 * and remove items from a queue. It is useful, for example, on tables
 * when we want to keep track of the items that are currently being edited.
 */
export class QueueState<T> {
  @observable
  private queue: T[] = [];

  constructor(
    private readonly compareUsing: (a: T, b: T) => boolean = (a, b) => a === b,
  ) {
    makeObservable(this);
    this.compareUsing = compareUsing;
  }

  private wrap(value: T | T[]) {
    return Array.isArray(value) ? value : [value];
  }

  @action.bound
  enqueue(items: T | T[]) {
    items = this.wrap(items);

    this.queue.push(
      ...(items
        .map((item) => (!this.isQueued(item) ? item : null))
        .filter(Boolean) as T[]),
    );
  }

  @action.bound
  dequeue(items: T | T[]) {
    const toRemove = this.wrap(items);

    this.queue = this.queue.filter(
      (value) => !toRemove.some((item) => this.compareUsing(value, item)),
    );
  }

  @action.bound
  async usingAsync<TResult>(
    enqueue: T | T[],
    callback: (this: QueueState<T>) => Promise<TResult>,
  ) {
    this.enqueue(enqueue);

    const result = await callback.call(this);

    this.dequeue(enqueue);

    return result;
  }

  isQueued(item: T): boolean {
    return this.queue.some((value) => this.compareUsing(value, item));
  }
}
